import { useCallback, useEffect, useState } from "react";

import { useQuery } from "@apollo/client/react";

import { AssistantNotesDocument } from "@app_schema";

import { type NotesState } from "@styled/assistant_notes";

import { type AssistantChats } from "./use_assistant_chats";
import { useEscape } from "./use_escape";
import { useRefetchOnActivity } from "./use_refetch_on_activity";

// "open" while showing; "fresh" once the answer in progress saved a note.
export const notesState = (shown: boolean, savedNow: boolean): NotesState => {
  if (shown) return "open";
  return savedNow ? "fresh" : "idle";
};

// The assistant's saved notes on the case, over the letters or pages. While
// it answers they follow it: open while it's in the notes, closed once it
// moves on. Otherwise the count, x or Esc toggle them. Only titles load;
// one note opens at a time: the one the assistant is in, or one clicked.
export const useAssistantNotes = ({
  projectId,
  chats,
  open,
}: {
  projectId: string;
  chats: AssistantChats;
  open: boolean;
}) => {
  const [shown, setShown] = useState(false);
  const skip = !open || !projectId;
  const { data, refetch } = useQuery(AssistantNotesDocument, {
    variables: { id: projectId },
    skip,
  });
  useRefetchOnActivity(
    skip ? undefined : chats.current?.id,
    skip ? undefined : String(chats.reads.writtenNotes ?? ""),
    refetch,
  );
  useEffect(() => {
    if (chats.running) setShown(false);
  }, [chats.running]);
  useEffect(() => setShown(false), [chats.current?.id]);
  const close = useCallback(() => setShown(false), []);
  useEscape(shown, close);
  const { noteNow, savedNoteNow, notesRead, currentNote } = chats.reads;
  const showing = shown || noteNow;
  // A note clicked open, until the assistant moves to another; "" closes all.
  const [opened, setOpened] = useState<string | null>(null);
  useEffect(() => setOpened(null), [currentNote]);
  const expandedKey = opened ?? currentNote;

  return {
    projectId,
    sections: data?.project.assistantNotes ?? [],
    shown: open && showing,
    currentKey: currentNote,
    expanded: (key: string) => key === expandedKey,
    toggleNote: (key: string) => setOpened(key === expandedKey ? "" : key),
    readAt: (key: string) => notesRead.get(key) ?? null,
    state: notesState(showing, savedNoteNow),
    locked: chats.running,
    toggle: () => setShown((value) => !value),
    close,
  } as const;
};

export type AssistantNotes = ReturnType<typeof useAssistantNotes>;

// The assistant's line shows once this chat has done something, or the case
// has notes: on both sides, the same rule.
export const showsAssistantLine = (
  chats: AssistantChats,
  notes: AssistantNotes,
) =>
  chats.running ||
  notes.sections.length > 0 ||
  (chats.reads.coverage?.read ?? 0) > 0;
